import axiosInstance from '../axios/axios-instance';
import { RewardEmailStatusDto } from 'shared/interfaces/user/my-rewards/reward-email-status.dto';
import { RewardHistoryDto } from 'shared/interfaces/user/my-rewards/reward-history.dto';
import { InitiateWithdrawalResponseDto } from 'shared/interfaces/withdrawal/initiate-withdrawal-response.dto';

/**
 * API for withdrawal.
 */
export const withdrawal = {
  /**
   * Retrieves the status of a reward email.
   * @param withdrawalId - The ID of the withdrawal to get the status for
   * @returns The status of the reward email
   */
  async getResendRewardEmailStatus(withdrawalId: number): Promise<RewardEmailStatusDto> {
    const response = await axiosInstance.get<RewardEmailStatusDto>(
      `/withdrawal-send/resend-reward-email-status?withdrawalId=${withdrawalId}`
    );
    return response.data;
  },

  /**
   * Redeems a reward.
   * @param paypalEmail - Optional PayPal email for PayPal withdrawals
   * @param verisoulSessionId - Optional Verisoul session ID for fraud prevention
   * @returns The ID of the created withdrawal send record
   */
  async redeem(paypalEmail?: string, verisoulSessionId?: string): Promise<InitiateWithdrawalResponseDto> {
    const body = paypalEmail ? { paypalEmail } : {};
    const config = verisoulSessionId ? { headers: { 'x-session-id': verisoulSessionId } } : {};
    const response = await axiosInstance.post<InitiateWithdrawalResponseDto>('/withdrawal/redeem', body, config);
    return response.data;
  },

  /**
   * Resends a reward email.
   * @param withdrawalId - The ID of the withdrawal to resend the reward email for
   * @returns The ID of the created withdrawal send record
   */
  async resendRewardEmail(withdrawalId: number): Promise<void> {
    await axiosInstance.post(`/withdrawal-send/resend-reward-email?withdrawalId=${withdrawalId}`);
  },

  /**
   * Retrieves the user's withdrawal history.
   * @param userId - The ID of the user to get the withdrawal history for.
   * @param page - The page number to retrieve.
   * @param pageSize - The number of items per page.
   * @returns A promise that resolves to the user's withdrawal history.
   */
  async getWithdrawalHistory(page: number, pageSize: number): Promise<RewardHistoryDto> {
    const response = await axiosInstance.get<RewardHistoryDto>('/withdrawal/history', {
      params: {
        page,
        pageSize,
      },
    });
    return response.data;
  },
};
